(() => {
  const batteryKey = 'fawn-ivory-phone-battery-start';
  const batteryDrainInterval = 3000;
  const minimumBattery = 5;

  const blushKeywords = [
    'birthday', 'party', 'club', 'dance', 'concert', 'festival', 'date',
    'dinner', 'brunch', 'wedding', 'drinks', 'bar', 'celebration', 'prom',
    'gala', 'rooftop', 'girls night', 'romantic', 'glam'
  ];
  const arcticKeywords = [
    'work', 'office', 'school', 'university', 'class', 'market', 'vintage',
    'shopping', 'museum', 'gallery', 'cafe', 'coffee', 'travel', 'airport',
    'picnic', 'lunch', 'casual', 'errands', 'cinema', 'movie', 'funeral'
  ];

  function bindOnce(element, key, eventName, callback) {
    if (!element || element.dataset[key] === 'true') return;
    element.dataset[key] = 'true';
    element.addEventListener(eventName, callback);
  }

  function announce(root, message) {
    const toast = root.querySelector('.status-toast');
    if (toast) toast.textContent = message;
  }

  function initSparkles(root) {
    const button = root.querySelector('.sparkle-chip');
    bindOnce(button, 'fiSparkleBound', 'click', () => {
      const enabled = root.classList.toggle('sparkle-on');
      button.setAttribute('aria-pressed', String(enabled));
      button.textContent = enabled ? '✦ sparkle on' : '♫ sparkle mode';
      announce(root, enabled ? 'Sparkle mode activated.' : 'Sparkle mode resting.');
    });
  }

  function initBattery(root) {
    const batteries = root.querySelectorAll('.battery');
    if (!batteries.length || root.dataset.fiBatteryTimer) return;

    const now = Date.now();
    let startedAt = now;
    try {
      const saved = Number(window.sessionStorage.getItem(batteryKey));
      if (Number.isFinite(saved) && saved > 0 && saved <= now) {
        startedAt = saved;
      } else {
        window.sessionStorage.setItem(batteryKey, String(now));
      }
    } catch (_) {
      startedAt = now;
    }

    const update = () => {
      const level = Math.max(
        minimumBattery,
        100 - Math.floor((Date.now() - startedAt) / batteryDrainInterval)
      );
      batteries.forEach((battery) => {
        battery.style.setProperty('--battery-level', `${level}%`);
        battery.setAttribute('aria-label', `Phone battery ${level}%`);
        const label = battery.querySelector('[data-fi-battery-label]');
        if (label) label.textContent = `${level}% ♡`;
      });
    };

    update();
    root.dataset.fiBatteryTimer = String(window.setInterval(update, 1000));
  }

  function initClock(root) {
    const clock = root.querySelector('[data-fi-clock]');
    if (!clock) return;
    const update = () => {
      clock.textContent = new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(new Date());
    };
    update();
    if (!root.dataset.fiClockTimer) {
      root.dataset.fiClockTimer = String(window.setInterval(update, 30000));
    }
  }

  function initSmoothLinks(scope) {
    scope.querySelectorAll('[data-fi-scroll]').forEach((link) => {
      bindOnce(link, 'fiScrollBound', 'click', (event) => {
        const url = new URL(link.href, window.location.href);
        if (url.pathname !== window.location.pathname || !url.hash) return;
        const target = document.querySelector(url.hash);
        if (!target) return;
        event.preventDefault();
        const mobile = window.matchMedia('(max-width: 700px)').matches;
        const offset = url.hash === '#hotline-phone' ? (mobile ? 12 : 104) : 88;
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        window.history.replaceState(null, '', url.hash);
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    });
  }

  function initTilt(root) {
    const stage = root.querySelector('.phone-stage');
    if (!stage || stage.dataset.fiTiltBound === 'true') return;
    stage.dataset.fiTiltBound = 'true';
    stage.addEventListener('pointermove', (event) => {
      if (window.matchMedia('(pointer: coarse)').matches) return;
      const rect = stage.getBoundingClientRect();
      stage.style.setProperty(
        '--tilt-x',
        `${(((event.clientY - rect.top) / rect.height) - 0.5) * -2.4}deg`
      );
      stage.style.setProperty(
        '--tilt-y',
        `${(((event.clientX - rect.left) / rect.width) - 0.5) * 3.5}deg`
      );
    });
    stage.addEventListener('pointerleave', () => {
      stage.style.setProperty('--tilt-x', '0deg');
      stage.style.setProperty('--tilt-y', '0deg');
    });
  }

  function initInformationTabs(root) {
    const panel = root.querySelector('[data-fi-tab-panel]');
    root.querySelectorAll('[data-fi-tab]').forEach((button) => {
      bindOnce(button, 'fiTabBound', 'click', () => {
        const wasActive = button.classList.contains('active');
        root.querySelectorAll('[data-fi-tab]').forEach((tab) => {
          tab.classList.remove('active');
          tab.setAttribute('aria-pressed', 'false');
          tab.setAttribute('aria-expanded', 'false');
        });
        if (!panel) return;
        if (wasActive) {
          panel.hidden = true;
          return;
        }
        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');
        button.setAttribute('aria-expanded', 'true');
        panel.querySelector('b').textContent = button.dataset.label || '';
        panel.querySelector('span').textContent = button.dataset.copy || '';
        panel.setAttribute('aria-label', `${button.dataset.label || ''} information`);
        panel.hidden = false;
      });
    });
  }

  function setVariant(root, button, shouldAnnounce = true) {
    const key = button.dataset.colour || 'arctic';
    const name = button.dataset.name || button.textContent.trim();
    const image = button.dataset.image;
    const alt = button.dataset.alt || name;
    const price = button.dataset.price || '';
    const available = button.dataset.available !== 'false';
    const variantId = button.dataset.variantId || '';

    root.querySelectorAll('[data-fi-variant]').forEach((swatch) => {
      const active = swatch === button;
      swatch.classList.toggle('active', active);
      swatch.setAttribute('aria-pressed', String(active));
    });
    root.querySelectorAll('[data-fi-product-image]').forEach((item) => {
      if (image) item.src = image;
      item.alt = alt;
    });
    root.querySelectorAll('[data-fi-selected-name]').forEach((item) => {
      item.textContent = name;
    });
    root.querySelectorAll('[data-fi-colour-caption]').forEach((item) => {
      item.textContent = `${name} · ${button.dataset.label || ''}`;
    });
    root.querySelectorAll('[data-fi-price]').forEach((item) => {
      if (price) item.textContent = price;
    });
    root.querySelectorAll('.product-variant-id').forEach((input) => {
      input.value = variantId;
      input.disabled = !available || !variantId;
    });
    root.querySelectorAll('[data-fi-add-button]').forEach((addButton) => {
      addButton.disabled = !available || !variantId;
      const mainLabel = addButton.querySelector('[data-fi-add-label]');
      if (mainLabel) mainLabel.textContent = available ? 'Add the Prowler to bag' : 'Sold out';
      const detail = addButton.querySelector('small');
      if (detail) detail.textContent = `${name}${price ? ` · ${price}` : ''}`;
    });
    root.dataset.selectedColour = key;
    if (shouldAnnounce) announce(root, `${name} selected. Your bag has not been added yet.`);
  }

  function initVariants(root) {
    const swatches = Array.from(root.querySelectorAll('[data-fi-variant]'));
    swatches.forEach((button) => {
      bindOnce(button, 'fiVariantBound', 'click', () => setVariant(root, button));
    });
    const selected = swatches.find((button) => button.classList.contains('active')) || swatches[0];
    if (selected) setVariant(root, selected, false);
  }

  function initHotline(root) {
    const question = root.querySelector('[data-fi-hotline-question]');
    const answer = root.querySelector('[data-fi-hotline-answer]');
    root.querySelectorAll('[data-fi-hotline]').forEach((button) => {
      bindOnce(button, 'fiHotlineBound', 'click', () => {
        root.querySelectorAll('[data-fi-hotline]').forEach((item) => {
          const active = item === button;
          item.classList.toggle('active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        if (question) question.textContent = button.dataset.label || '';
        if (answer) answer.textContent = button.dataset.copy || '';
      });
    });
  }

  function recommendationFor(value) {
    const eventName = value.trim();
    const normalized = eventName.toLocaleLowerCase();
    const score = (keywords) => keywords.reduce(
      (total, keyword) => total + (normalized.includes(keyword) ? 1 : 0),
      0
    );
    const blushScore = score(blushKeywords);
    const arcticScore = score(arcticKeywords);
    const tieBreaker = Array.from(normalized).reduce(
      (total, character) => total + character.charCodeAt(0),
      0
    );
    const colour = blushScore === arcticScore
      ? (tieBreaker % 2 === 0 ? 'blush' : 'arctic')
      : (blushScore > arcticScore ? 'blush' : 'arctic');
    const name = colour === 'blush' ? 'Blush Pink' : 'Arctic Grey';
    return {
      colour,
      name,
      eventName,
      label: `your plan · ${eventName}`,
      title: `Your match: ${name}.`,
      copy: colour === 'blush'
        ? `For ${eventName}, the soft pink leopard cuff brings playful, flash-photo energy while the black leather keeps it sharp.`
        : `For ${eventName}, the cooler leopard cuff feels sleek and effortless, letting the studs and ruched black leather lead the look.`
    };
  }

  function revealMatch(root, data) {
    const result = root.querySelector('[data-fi-match-result]');
    if (!result) return;
    result.classList.add('match-revealed');
    const sticker = result.querySelector('[data-fi-match-sticker]');
    const label = result.querySelector('[data-fi-match-label]');
    const title = result.querySelector('[data-fi-match-title]');
    const copy = result.querySelector('[data-fi-match-copy]');
    const image = result.querySelector('[data-fi-match-image]');
    if (sticker) sticker.textContent = `match found · ${data.name || data.colour} ✦`;
    if (label) label.textContent = data.label || '';
    if (title) title.textContent = data.title || '';
    if (copy) copy.textContent = data.copy || '';
    const heroRoot = document.querySelector('[data-fi-prowler]');
    const swatch = heroRoot?.querySelector(`[data-fi-variant][data-colour="${data.colour}"]`);
    if (image && swatch?.dataset.image) {
      image.src = swatch.dataset.image;
      image.alt = swatch.dataset.alt || data.name || '';
    }
    result.dataset.colour = data.colour;
  }

  function initMatcher(root) {
    const form = root.querySelector('[data-fi-event-form]');
    const confirmation = root.querySelector('[data-fi-match-confirmation]');
    bindOnce(form, 'fiMatcherBound', 'submit', (event) => {
      event.preventDefault();
      const input = form.querySelector('input');
      if (!input?.value.trim()) return;
      const data = recommendationFor(input.value);
      revealMatch(root, data);
      if (confirmation) {
        confirmation.hidden = false;
        confirmation.querySelector('strong').textContent = data.name;
        confirmation.querySelector('small').textContent = `recommended for “${data.eventName}”`;
      }
      const heroRoot = document.querySelector('[data-fi-prowler]');
      const swatch = heroRoot?.querySelector(`[data-fi-variant][data-colour="${data.colour}"]`);
      if (heroRoot && swatch) setVariant(heroRoot, swatch, false);
      if (window.matchMedia('(max-width: 1040px)').matches) {
        window.setTimeout(() => root.querySelector('[data-fi-match-result]')?.scrollIntoView({
          behavior: 'smooth', block: 'center'
        }), 100);
      }
    });

    root.querySelectorAll('[data-fi-plan]').forEach((button) => {
      bindOnce(button, 'fiPlanBound', 'click', () => {
        root.querySelectorAll('[data-fi-plan]').forEach((item) => {
          item.classList.toggle('active', item === button);
        });
        if (confirmation) confirmation.hidden = true;
        revealMatch(root, {
          colour: button.dataset.colour,
          name: button.dataset.name,
          label: button.dataset.label,
          title: button.dataset.title,
          copy: button.dataset.copy
        });
        const heroRoot = document.querySelector('[data-fi-prowler]');
        const swatch = heroRoot?.querySelector(`[data-fi-variant][data-colour="${button.dataset.colour}"]`);
        if (heroRoot && swatch) setVariant(heroRoot, swatch, false);
      });
    });

    const loadButton = root.querySelector('[data-fi-load-match]');
    bindOnce(loadButton, 'fiLoadBound', 'click', () => {
      const result = root.querySelector('[data-fi-match-result]');
      const colour = result?.dataset.colour;
      const heroRoot = document.querySelector('[data-fi-prowler]');
      const swatch = heroRoot?.querySelector(`[data-fi-variant][data-colour="${colour}"]`);
      if (heroRoot && swatch) setVariant(heroRoot, swatch, false);
      document.querySelector('#shop')?.scrollIntoView({ behavior: 'smooth' });
    });
  }

  function initCollectionCards(scope) {
    scope.querySelectorAll('[data-fi-product-card]').forEach((card) => {
      card.querySelectorAll('[data-fi-card-variant]').forEach((button) => {
        bindOnce(button, 'fiCardVariantBound', 'click', () => {
          card.querySelectorAll('[data-fi-card-variant]').forEach((item) => {
            item.classList.toggle('active', item === button);
            item.setAttribute('aria-pressed', String(item === button));
          });
          const image = card.querySelector('[data-fi-card-image]');
          if (image && button.dataset.image) image.src = button.dataset.image;
          const label = card.querySelector('[data-fi-card-colour]');
          if (label) label.textContent = button.dataset.name || '';
        });
      });
    });
  }

  function initMotion(scope) {
    const selector = [
      '.hero-shell > .side-column > .window', '.hero-shell > .phone-stage',
      '.hotline-grid > *', '.match-section > *', '.section-heading',
      '.preorder-cards > li', '.policy-strip', '.faq-grid > *',
      '.contact-section > *', '.placeholder-card'
    ].join(',');
    const elements = Array.from(scope.querySelectorAll(selector)).filter(
      (element) => element.dataset.fiMotion !== 'true'
    );
    elements.forEach((element, index) => {
      element.dataset.fiMotion = 'true';
      element.classList.add('motion-reveal');
      element.style.setProperty('--motion-delay', `${(index % 4) * 70}ms`);
    });
    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('motion-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('motion-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -7% 0px' });
    window.requestAnimationFrame(() => elements.forEach((element) => observer.observe(element)));
  }

  function initSite(scope = document) {
    scope.querySelectorAll('[data-fi-prowler]').forEach((root) => {
      initSparkles(root);
      initBattery(root);
      initClock(root);
      initTilt(root);
      initInformationTabs(root);
      initVariants(root);
      initSmoothLinks(root);
      initMotion(root);
    });
    scope.querySelectorAll('[data-fi-hotline-root]').forEach(initHotline);
    scope.querySelectorAll('[data-fi-match-root]').forEach(initMatcher);
    initSmoothLinks(scope);
    initCollectionCards(scope);
    initMotion(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSite());
  } else {
    initSite();
  }
  document.addEventListener('shopify:section:load', () => initSite(document));
})();
